import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Contact,
  Info,
  Loader2,
  LockKeyhole,
  Share2,
  ShieldCheck,
  TriangleAlert,
  X,
} from "lucide-react";
import { MNOS, normaliseMsisdn, type DetectResult, type MnoCode, type Order } from "@evocha/shared-types";
import { api } from "../lib/api";
import { useWallet, errorMessage } from "../lib/queries";
import { useSession } from "../lib/session";
import { tzs } from "../lib/format";

const PRESETS = [500, 1000, 2000, 5000, 10000, 20000, 50000];
const COMMISSION_BPS = 320;

// Mirrors apps/api/scripts/seed_catalogue.py so the chip still renders while
// the server-side detect call is in flight (or if it fails).
const PREFIX: Record<string, MnoCode> = {
  "61": "halotel", "62": "halotel",
  "65": "tigo", "67": "tigo", "71": "tigo",
  "68": "airtel", "69": "airtel", "78": "airtel",
  "73": "ttcl",
  "74": "vodacom", "75": "vodacom", "76": "vodacom", "79": "vodacom",
  "77": "tigo",
};

function detectLocal(input: string): MnoCode | null {
  const n = normaliseMsisdn(input);
  if (!n) return null;
  const block = n.slice(3, 5);
  if (block === "77" && "4567".includes(n[5] ?? "")) return "zantel";
  return PREFIX[block] ?? null;
}

function formatInput(raw: string): string {
  let d = raw.replace(/\D/g, "");
  if (d.startsWith("00255")) d = d.slice(5);
  else if (d.startsWith("255")) d = d.slice(3);
  else if (d.startsWith("0")) d = d.slice(1);
  d = d.slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

export function Sell() {
  const { agent } = useSession();
  const wallet = useWallet();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState<number | null>(1000);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [detected, setDetected] = useState<DetectResult | null>(null);
  const [detecting, setDetecting] = useState(false);

  const normalised = normaliseMsisdn(phone);
  const localCode = useMemo(() => detectLocal(phone), [phone]);

  useEffect(() => {
    if (!normalised) {
      setDetected(null);
      setDetecting(false);
      return;
    }
    let alive = true;
    setDetected(null);
    setDetecting(true);
    const timer = window.setTimeout(() => {
      api
        .detect(normalised)
        .then((result) => {
          if (alive) setDetected(result);
        })
        .catch(() => {
          if (alive) setDetected(null);
        })
        .finally(() => {
          if (alive) setDetecting(false);
        });
    }, 350);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [normalised]);

  const mno = useMemo(() => {
    if (detected?.mno && detected.valid) {
      const code = detected.mno.code as MnoCode;
      return {
        code,
        name: detected.mno.name,
        colour: detected.mno.colour ?? MNOS[code]?.colour ?? "#64748b",
      };
    }
    // A server rejection is authoritative; never fall back to the local hint.
    if (detected && !detected.valid) return null;
    return localCode ? MNOS[localCode] : null;
  }, [detected, localCode]);

  const limits = useMemo(() => {
    const txnMinor = Number(agent?.limit_txn_minor ?? 5_000_000);
    const fallback = {
      min_major: 500,
      max_major: Math.min(Math.floor(txnMinor / 100), 50_000),
    };
    if (!detected?.valid) return fallback;
    return {
      min_major: Math.max(Math.floor(Number(detected.limits.min_minor) / 100), 1),
      max_major: Math.min(
        Math.floor(Number(detected.limits.max_minor) / 100),
        Math.floor(txnMinor / 100),
      ),
    };
  }, [detected, agent]);

  const chosen = custom !== "" ? Number(custom.replace(/[^\d]/g, "")) || 0 : amount ?? 0;
  const amountMinor = chosen * 100;
  const commissionMinor = Math.round((amountMinor * COMMISSION_BPS) / 10_000);
  const masked = normalised ? `${normalised.slice(0, 6)}***${normalised.slice(-3)}` : "";
  const available = Number(wallet.data?.available_minor ?? 0);
  const canAfford = amountMinor > 0 && amountMinor <= available;
  const withinLimits = chosen >= limits.min_major && chosen <= limits.max_major;
  const digits = phone.replace(/\D/g, "").length;
  const numberComplete = digits >= 9;
  const blockedReason =
    numberComplete && !detecting && !mno
      ? detected?.reason === "unknown_prefix"
        ? "That number is not on a supported network yet. Check the digits — Tanzanian mobiles start 06x, 07x or 08x."
        : "We could not confirm the network for this number. Check the digits and try again."
      : null;

  function next() {
    setError(null);
    if (step === 1 && (!normalised || !mno)) return;
    if (step === 2 && !chosen) return;
    setStep((s) => Math.min(3, s + 1));
  }

  async function confirm() {
    if (!normalised || !mno || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createOrder({
        msisdn: normalised,
        mno: mno.code,
        amount_minor: amountMinor,
        channel: "web",
      });
      setOrder(created);
      void wallet.refetch();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setOrder(null);
    setStep(1);
    setPhone("");
    setAmount(1000);
    setCustom("");
    setError(null);
    setDetected(null);
  }

  if (agent && agent.kyc_status !== "approved") {
    const statusLabel = (agent.kyc_status || "not_submitted").replace(/_/g, " ");
    return (
      <div className="sell-layout">
        <section className="ev-card sell-main">
          <div className="sell-body kyc-locked">
            <span className="kyc-locked-icon" aria-hidden>
              <LockKeyhole size={26} />
            </span>
            <span className="ev-eyebrow">Selling locked</span>
            <h2 className="ev-h2" style={{ marginTop: 6 }}>Selling unlocks after KYC approval</h2>
            <p className="ev-muted kyc-locked-copy">
              Your KYC is currently <strong>{statusLabel}</strong>. A compliance admin reviews
              submitted documents and approves the account. Once approved you can sell airtime
              and earn commission.
            </p>
            <div className="kyc-locked-actions">
              <Link to="/profile" className="ev-btn ev-btn-primary">
                <ShieldCheck size={16} /> View KYC status
              </Link>
              <Link to="/help" className="ev-btn ev-btn-ghost">Contact support</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (order) {
    const success = order.status === "SUCCESS";
    const pending = order.status === "PENDING" || order.status === "MANUAL_REVIEW";
    return (
      <div className="sell-done">
        <section className="ev-card receipt">
          <div className="receipt-top">
            <span className="receipt-check" style={success ? undefined : { background: "var(--ev-amber-100, #fef3c7)", color: "var(--ev-amber-700, #b45309)" }}>
              {success ? <Check size={26} strokeWidth={2.6} /> : pending ? <Info size={24} /> : <TriangleAlert size={24} />}
            </span>
            <span className={`ev-badge ${success ? "ev-badge-success" : pending ? "ev-badge-pending" : "ev-badge-danger"}`}>
              {order.status_label}
            </span>
            <h2 className="receipt-amount">{order.amount_display}</h2>
            <p className="ev-muted">to {order.msisdn_masked} · {order.mno.name}</p>
          </div>

          <dl className="receipt-rows">
            <div><dt>Commission earned</dt><dd style={{ color: "var(--ev-mint-600)" }}>+{order.commission_display}</dd></div>
            <div><dt>Reference</dt><dd className="ev-mono">{order.ref}</dd></div>
            <div><dt>Date</dt><dd>{new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(order.created_at))}</dd></div>
            {order.balance_after && (
              <div><dt>Balance after</dt><dd>{tzs(order.balance_after.available_minor)}</dd></div>
            )}
            <div><dt>Service fee</dt><dd>{tzs(order.fee_minor)}</dd></div>
          </dl>

          {order.message && (
            <div className={`drawer-alert drawer-alert-${pending ? "info" : success ? "success" : "danger"}`} style={{ margin: "0 24px 12px" }}>
              <Info size={16} />
              <p>{order.message}</p>
            </div>
          )}

          <div className="receipt-actions">
            <button className="ev-btn ev-btn-primary ev-btn-block" onClick={reset}>New sale</button>
            <button
              className="ev-btn ev-btn-secondary"
              onClick={() => void navigator.clipboard?.writeText(order.ref).catch(() => undefined)}
            >
              <Share2 size={16} /> Copy reference
            </button>
          </div>
          <Link to="/transactions" className="receipt-link">View in transactions <ArrowRight size={14} /></Link>
        </section>

        <aside className="sell-side">
          <div className="ev-card ev-card-pad">
            <span className="ev-eyebrow">What happens next</span>
            <p className="side-copy">
              {success
                ? "The airtime is already with the customer. Your commission is credited to the commission bucket and can be moved to your available balance."
                : pending
                  ? "We have reserved the value and are checking with the network. Do not repeat this sale — we will resolve it automatically."
                  : "No airtime was sent and the reserved amount has been returned to your available balance."}
            </p>
          </div>
          <div className="ev-card ev-card-pad">
            <span className="ev-eyebrow">Receipt safety</span>
            <p className="side-copy">Anyone can verify this sale with the reference <strong className="ev-mono">{order.ref}</strong> — no phone number or balance is exposed.</p>
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="sell-layout">
      <section className="ev-card sell-main">
        <div className="sell-head">
          <div>
            <span className="ev-eyebrow">Sell airtime</span>
            <h2 className="ev-h2" style={{ marginTop: 4 }}>Who are you topping up?</h2>
          </div>
          <div className="stepper" aria-label={`Step ${step} of 3`}>
            {[1, 2, 3].map((n) => (
              <span key={n} className={`step-dot ${step >= n ? "step-dot-on" : ""}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div className="sell-body">
            <label className="ev-field">
              <span className="ev-label">Customer mobile number</span>
              <div className="ev-input-affix">
                <span className="ev-affix ev-affix-left">+255</span>
                <input
                  className="ev-input input-lg"
                  inputMode="tel"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(formatInput(e.target.value))}
                  placeholder="712 345 678"
                />
              </div>
              <span className="ev-hint">We detect the network automatically. You can still change it.</span>
            </label>

            <div className="detect-row">
              {mno ? (
                <>
                  <span className="network-chip network-detected">
                    <span className="mno-dot" style={{ background: mno.colour }} />
                    {mno.name}
                    <em>Detected</em>
                  </span>
                  {detecting ? (
                    <span className="detect-wait"><Loader2 size={14} className="spin" /> Checking number…</span>
                  ) : (
                    <span className="detect-ok"><CheckCircle2 size={15} /> Number looks good</span>
                  )}
                </>
              ) : digits === 0 ? (
                <span className="detect-wait">Works with Vodacom, Airtel, Tigo, Halotel, Zantel and TTCL.</span>
              ) : !numberComplete ? (
                <span className="detect-wait">Keep typing to detect the network…</span>
              ) : detecting ? (
                <span className="detect-wait"><Loader2 size={14} className="spin" /> Checking number…</span>
              ) : (
                <span className="detect-bad"><TriangleAlert size={15} /> {blockedReason ?? "This number cannot be used."}</span>
              )}
            </div>

            <button className="contact-btn" type="button" disabled title="Contact picker is available in the mobile app">
              <Contact size={18} />
              <span>Choose from contacts</span>
              <span className="contact-hint">Mobile app only</span>
            </button>

            <div className="sell-actions">
              <Link to="/" className="ev-btn ev-btn-ghost"><ArrowLeft size={16} /> Cancel</Link>
              <button className="ev-btn ev-btn-primary ev-btn-lg" onClick={next} disabled={!normalised || !mno || detecting}>
                Continue <ArrowRight size={17} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="sell-body">
            <div className="sell-context">
              <span className="network-chip">
                <span className="mno-dot" style={{ background: mno?.colour }} />
                {mno?.name}
              </span>
              <span className="ev-muted">to {masked}</span>
              <button className="link-button" onClick={() => setStep(1)}>Change</button>
            </div>

            <div className="amount-head">
              <span className="ev-label">Amount</span>
              <span className="ev-hint">
                Minimum {tzs(limits.min_major * 100)} · Maximum {tzs(limits.max_major * 100)}
              </span>
            </div>

            <div className="amount-grid">
              {PRESETS.filter((value) => value >= limits.min_major && value <= limits.max_major).map((value) => {
                const active = custom === "" && amount === value;
                return (
                  <button
                    key={value}
                    className={`amount-tile ${active ? "amount-tile-active" : ""}`}
                    onClick={() => { setAmount(value); setCustom(""); }}
                  >
                    <strong>{new Intl.NumberFormat("en-TZ").format(value)}</strong>
                    <span>TZS</span>
                  </button>
                );
              })}
              <button className={`amount-tile ${custom !== "" ? "amount-tile-active" : ""}`} onClick={() => { setAmount(null); setCustom(custom || ""); }}>
                <strong>Other</strong>
                <span>amount</span>
              </button>
            </div>

            {custom !== "" && (
              <label className="ev-field" style={{ marginTop: 16 }}>
                <span className="ev-label">Custom amount (TZS)</span>
                <div className="ev-input-affix">
                  <span className="ev-affix ev-affix-left">TZS</span>
                  <input
                    className="ev-input"
                    inputMode="numeric"
                    autoFocus
                    value={custom}
                    onChange={(e) => setCustom(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="0"
                  />
                </div>
              </label>
            )}

            <div className="earn-note">
              <ShieldCheck size={16} />
              <span>You earn <strong>{tzs(commissionMinor)}</strong> commission on this sale.</span>
            </div>

            {chosen > 0 && !withinLimits && (
              <div className="pending-note">
                <TriangleAlert size={16} />
                <span>Enter an amount between <strong>{tzs(limits.min_major * 100)}</strong> and <strong>{tzs(limits.max_major * 100)}</strong>.</span>
              </div>
            )}

            <div className="sell-actions">
              <button className="ev-btn ev-btn-ghost" onClick={() => setStep(1)}><ArrowLeft size={16} /> Back</button>
              <button className="ev-btn ev-btn-primary ev-btn-lg" onClick={next} disabled={!withinLimits}>
                Review sale <ArrowRight size={17} />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="sell-body">
            <div className="review-card">
              <div className="review-row review-total">
                <span>Airtime amount</span>
                <strong>{tzs(amountMinor)}</strong>
              </div>
              <div className="review-row"><span>Customer</span><strong>{masked}</strong></div>
              <div className="review-row"><span>Network</span><strong>{mno?.name}</strong></div>
              <div className="review-row"><span>Your commission</span><strong style={{ color: "var(--ev-mint-600)" }}>+{tzs(commissionMinor)}</strong></div>
              <div className="review-row"><span>Service fee</span><strong>{tzs(0)}</strong></div>
              <div className="review-row">
                <span>Available after</span>
                <strong>{tzs(Math.max(available - amountMinor, 0))}</strong>
              </div>
            </div>

            {!canAfford && (
              <div className="pending-note">
                <TriangleAlert size={16} />
                <span>Your available balance ({tzs(available)}) is below this sale. Top up your wallet first.</span>
              </div>
            )}

            <div className="pending-note">
              <Info size={16} />
              <span>If the network is slow, this sale will show <strong>Pending — do not repeat</strong>. We check the status for you.</span>
            </div>

            <div className="sell-actions">
              <button className="ev-btn ev-btn-ghost" onClick={() => setStep(2)}><ArrowLeft size={16} /> Back</button>
              <Link to="/wallet" className="ev-btn ev-btn-secondary">Top up wallet</Link>
              <button className="ev-btn ev-btn-gradient ev-btn-lg" onClick={() => void confirm()} disabled={!canAfford || busy}>
                {busy ? <><Loader2 size={17} className="spin" /> Sending airtime…</> : <><LockKeyhole size={17} /> Confirm sale</>}
              </button>
            </div>
          </div>
        )}
      </section>

      <aside className="sell-side">
        <div className="ev-card ev-card-pad summary-card">
          <span className="ev-eyebrow">Sale summary</span>
          <div className="summary-amount">{chosen ? tzs(amountMinor) : "TZS 0"}</div>
          <div className="summary-network">
            {mno ? (
              <><span className="mno-dot" style={{ background: mno.colour }} /> {mno.name} · {masked || "no number yet"}</>
            ) : (
              "Waiting for a number"
            )}
          </div>
          <hr className="ev-divider" />
          <div className="summary-row"><span>Commission</span><strong style={{ color: "var(--ev-mint-600)" }}>+{tzs(commissionMinor)}</strong></div>
          <div className="summary-row"><span>Available now</span><strong>{tzs(available)}</strong></div>
          <div className="summary-tip">
            <ShieldCheck size={15} />
            <span>Funds are reserved before the network call. You can never overspend.</span>
          </div>
        </div>

        <div className="sell-tip">
          <span className="sell-tip-icon">Tip</span>
          <p>Double-check the last three digits with the customer. Airtime cannot be reversed once delivered.</p>
        </div>
      </aside>

      {error && (
        <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Sale failed">
          <div className="pin-modal">
            <button className="ev-icon-btn pin-close" onClick={() => setError(null)} aria-label="Close"><X size={18} /></button>
            <span className="pin-lock" style={{ background: "var(--ev-danger-50, #fef2f2)", color: "var(--ev-danger-600, #dc2626)" }}><TriangleAlert size={20} /></span>
            <h3 className="ev-h3">Sale not completed</h3>
            <p className="ev-muted ev-center" style={{ marginTop: 6 }}>{error}</p>
            <button className="ev-btn ev-btn-primary ev-btn-block" onClick={() => setError(null)}>Try again</button>
          </div>
        </div>
      )}
    </div>
  );
}
